import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  HEARINGROLE_OWNER,
  HEARINGROLE_TYPE,
  HEARING_TYPE,
  USERHEARINGROLE_TYPE,
  USER_TYPE,
} from '../../constants/api';
import {
  createUserHearingRoleInitialize,
  getAllHearingsInitialize,
  removeUserHearingRolesFromStore,
  forceDeleteHearingInitialize,
  removeCommentsFromStore,
  removeContentsFromStore,
} from '../actions/api';
import { hearingRoleByRoleSelector } from '../apiFeatures/hearingRoleSlice';
import { userHearingRoleOwnerByHearingId } from '../apiFeatures/userHearingRoleSlice';
import { parseApiError } from '../globalMessageSlice';
import { getEntityId, getRelIds } from '../helpers/apiHelpers';
import { awaitApiCall } from '../helpers/sagaHelpers';
import { hearingByIdSelector } from '../apiFeatures/hearingSlice';
import { allCommentsByHearingIdSelector } from '../apiFeatures/commentSlice';

const initialState = {
  isChangingHearingOwner: false,
  isPanelOpen: false,
  selectedHearingId: null,
  isDeletingHearing: false,
};

// ----------- Actions -------------
const setIsChangingHearingOwner = createAction(
  'page/administration/setIsChangingHearingOwner',
);
const setIsPanelOpen = createAction('page/administration/setIsPanelOpen');
const setSelectedHearingId = createAction(
  'page/administration/setSelectedHearingId',
);
const changeHearingOwnerInitialize = createAction(
  'page/administration/changeHearingOwner/initialize',
);
const initializeState = createAction('page/administration/initializeState');

const setIsDeletingHearing = createAction(
  'page/administration/setIsDeletingHearing',
);

const forceDeleteHearing = createAction(
  'page/administration/forceDeleteHearing',
);

// ----------- Reducers -------------
const administrationPageReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setIsChangingHearingOwner, (state, action) => {
      state.isChangingHearingOwner = action.payload;
    })
    .addCase(setIsPanelOpen, (state, action) => {
      state.isPanelOpen = action.payload;
    })
    .addCase(setSelectedHearingId, (state, action) => {
      state.selectedHearingId = action.payload;
    })
    .addCase(setIsDeletingHearing, (state, action) => {
      state.isDeletingHearing = action.payload;
    })
    .addCase(initializeState, () => initialState);
});

// ----------- Sagas -------------
export function* administrationPageSaga() {
  yield all([fork(changeHearingOwnerSaga), fork(forceDeleteHearingSaga)]);
}

function* changeHearingOwnerSaga() {
  yield takeLatest(changeHearingOwnerInitialize.type, function* (action) {
    yield put(setIsChangingHearingOwner(true));

    const { hearingId, userId } = action.payload;
    const hearingRoleOwner = yield select(
      hearingRoleByRoleSelector(HEARINGROLE_OWNER),
    );
    const currentUserHearingRole = yield select(
      userHearingRoleOwnerByHearingId(hearingId),
    );

    const userHearingRoleModel = {
      type: USERHEARINGROLE_TYPE,
      attributes: {},
      relationships: {
        hearing: {
          data: {
            id: hearingId,
            type: HEARING_TYPE,
          },
        },
        hearingRole: {
          data: {
            id: getEntityId(hearingRoleOwner),
            type: HEARINGROLE_TYPE,
          },
        },
        user: {
          data: {
            id: userId,
            type: USER_TYPE,
          },
        },
      },
    };

    const createUserHearingRoleInitAction = yield put(
      createUserHearingRoleInitialize({
        hearingId,
        model: userHearingRoleModel,
      }),
    );

    const createUserHearingRoleResult = yield awaitApiCall(
      createUserHearingRoleInitAction,
    );

    if (createUserHearingRoleResult.success) {
      yield put(
        removeUserHearingRolesFromStore(getEntityId(currentUserHearingRole)),
      );
      yield put(getAllHearingsInitialize());
      yield put(setIsPanelOpen(false));
      yield put(setSelectedHearingId(null));
    } else if (createUserHearingRoleResult.error) {
      yield put(parseApiError(createUserHearingRoleResult.error.payload));
    }

    yield put(setIsChangingHearingOwner(false));
  });
}

function* forceDeleteHearingSaga() {
  yield takeLatest(forceDeleteHearing.type, function* (action) {
    const hearingId = action.payload;
    if (!hearingId) {
      return;
    }

    yield put(setIsDeletingHearing(true));

    const hearing = yield select(hearingByIdSelector(hearingId));
    const comments = yield select(allCommentsByHearingIdSelector(hearingId));

    const userHearingRoleIds = getRelIds(hearing, 'userHearingRoles');
    const contentIds = getRelIds(hearing, 'contents');
    const commentIds = comments.map((comment) => getEntityId(comment));

    const initAction = yield put(forceDeleteHearingInitialize({ hearingId }));
    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(removeContentsFromStore(contentIds));
      yield put(removeUserHearingRolesFromStore(userHearingRoleIds));
      yield put(removeCommentsFromStore(commentIds));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsDeletingHearing(false));
  });
}

// ----------- Selectors -----------
const selectAdministrationState = (state) => state.page.administration;

const isChangingHearingOwnerSelector = createSelector(
  selectAdministrationState,
  (state) => state.isChangingHearingOwner,
);

const isPanelOpenSelector = createSelector(
  selectAdministrationState,
  (state) => state.isPanelOpen,
);

const selectedHearingIdSelector = createSelector(
  selectAdministrationState,
  (state) => state.selectedHearingId,
);

const isDeletingHearingSelector = createSelector(
  selectAdministrationState,
  (state) => state.isDeletingHearing,
);

export {
  isChangingHearingOwnerSelector,
  changeHearingOwnerInitialize,
  setIsPanelOpen,
  setSelectedHearingId,
  isPanelOpenSelector,
  selectedHearingIdSelector,
  initializeState,
  isDeletingHearingSelector,
  forceDeleteHearing,
};

export default administrationPageReducer;
